package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIcon
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.BusinessCover
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReportReason
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReportSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.ReportStatus
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.Tale
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_copied
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_copy_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_error
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_like_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_more_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_done
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_failed
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_reason_offensive
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_reason_other
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_reason_spam
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_sent
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_submit
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_report_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_save_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_share_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_tale_image_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_tale_moral_label
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_tale_not_found
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_share_message
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** Stateful entry for a Shahnameh tale (`/gems/shahnameh/{id}`). */
@Composable
fun TaleDetailRoute(
    taleId: String,
    onBack: () -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: TaleDetailViewModel = koinViewModel { parametersOf(taleId) },
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val shareService = koinInject<ShareService>()
    val shareConfig = koinInject<ShareConfig>()
    val lang = AppTheme.language

    LaunchedEffect(state.signInRequired) {
        if (state.signInRequired) { viewModel.consumeSignInGate(); onRequireSignIn() }
    }
    LaunchedEffect(state.reportStatus) {
        if (state.reportStatus == GemReportStatus.SignInRequired) { viewModel.resetReport(); onRequireSignIn() }
    }
    val shareTemplate = stringResource(Res.string.gems_share_message)
    TaleDetailScreen(
        state = state,
        onBack = onBack,
        onToggleLike = viewModel::toggleLike,
        onToggleSave = viewModel::toggleSave,
        onShare = { tale ->
            shareService.share(
                shareTemplate
                    .replace("%1\$s", tale.title.resolve(lang))
                    .replace("%2\$s", shareConfig.taleLink(tale.id)),
            )
        },
        onReport = viewModel::report,
        onResetReport = viewModel::resetReport,
        modifier = modifier,
    )
}

/** The full tale reading view: hero art → title → character chips → story → moral → action rail. */
@Composable
fun TaleDetailScreen(
    state: TaleDetailUiState,
    onBack: () -> Unit,
    onToggleLike: () -> Unit,
    onToggleSave: () -> Unit,
    onShare: (Tale) -> Unit,
    onReport: (reason: String) -> Unit,
    onResetReport: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    val clipboard = LocalClipboardManager.current
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val copiedMessage = stringResource(Res.string.gems_copied)
    var showReport by remember { mutableStateOf(false) }

    Box(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        Column(modifier = Modifier.fillMaxSize().statusBarsPadding().navigationBarsPadding()) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.sm),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                AppIconButton(
                    iconName = "arrow_back",
                    contentDescription = stringResource(Res.string.gems_back_a11y),
                    onClick = onBack,
                    tint = AppTheme.colors.onSurface,
                )
            }
            when {
                state.loading -> LoadingState()
                state.error || state.tale == null -> EmptyState(
                    title = if (state.error) stringResource(Res.string.gems_error) else stringResource(Res.string.gems_tale_not_found),
                )
                else -> {
                    val tale = state.tale
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                            .verticalScroll(rememberScrollState())
                            .padding(bottom = AppTheme.spacing.xl),
                        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                    ) {
                        Box(modifier = Modifier.fillMaxWidth().aspectRatio(16f / 9f)) {
                            BusinessCover(
                                imageUrl = tale.imageUrl,
                                iconName = "menu_book",
                                contentDescription = stringResource(Res.string.gems_tale_image_a11y),
                            )
                        }
                        Column(
                            modifier = Modifier.padding(horizontal = AppTheme.spacing.lg),
                            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                        ) {
                            Text(
                                text = tale.title.resolve(lang),
                                style = AppTheme.typography.headlineLg,
                                color = AppTheme.colors.onSurface,
                            )
                            // Action row (like / save / copy / share / report).
                            Row(horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm), verticalAlignment = Alignment.CenterVertically) {
                                AppIconButton(
                                    iconName = "favorite",
                                    contentDescription = stringResource(Res.string.gems_like_a11y),
                                    onClick = onToggleLike,
                                    filled = state.liked,
                                    tint = if (state.liked) AppTheme.colors.error else AppTheme.colors.onSurfaceVariant,
                                )
                                Text(text = lang.formatCount(tale.likeCount), style = AppTheme.typography.labelSm, color = AppTheme.colors.onSurfaceVariant)
                                AppIconButton(
                                    iconName = "bookmark",
                                    contentDescription = stringResource(Res.string.gems_save_a11y),
                                    onClick = onToggleSave,
                                    filled = state.saved,
                                    tint = if (state.saved) AppTheme.colors.primary else AppTheme.colors.onSurfaceVariant,
                                )
                                AppIconButton(
                                    iconName = "content_copy",
                                    contentDescription = stringResource(Res.string.gems_copy_a11y),
                                    onClick = {
                                        clipboard.setText(AnnotatedString(tale.body.resolve(lang)))
                                        scope.launch { snackbarHostState.showSnackbar(copiedMessage) }
                                    },
                                    tint = AppTheme.colors.onSurfaceVariant,
                                )
                                AppIconButton(
                                    iconName = "share",
                                    contentDescription = stringResource(Res.string.gems_share_a11y),
                                    onClick = { onShare(tale) },
                                    tint = AppTheme.colors.onSurfaceVariant,
                                )
                                AppIconButton(
                                    iconName = "more_vert",
                                    contentDescription = stringResource(Res.string.gems_more_a11y),
                                    onClick = { showReport = true },
                                    tint = AppTheme.colors.onSurfaceVariant,
                                )
                            }
                            Text(
                                text = tale.body.resolve(lang),
                                style = AppTheme.typography.bodyLg,
                                color = AppTheme.colors.onSurface,
                            )
                            tale.moral?.resolve(lang)?.takeIf { it.isNotBlank() }?.let { moral ->
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .background(AppTheme.colors.primaryContainer, AppTheme.shapes.card)
                                        .padding(AppTheme.spacing.md),
                                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                                ) {
                                    Text(
                                        text = stringResource(Res.string.gems_tale_moral_label),
                                        style = AppTheme.typography.labelSm,
                                        // On the teal primaryContainer fill, page-surface tokens are
                                        // near-invisible — use the on-teal pair (light-cyan label,
                                        // white body) that every filled element in the app uses.
                                        color = AppTheme.colors.onPrimaryContainer,
                                    )
                                    Text(
                                        text = moral,
                                        style = AppTheme.typography.bodyMd,
                                        color = AppTheme.colors.onPrimary,
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
        SnackbarHost(hostState = snackbarHostState, modifier = Modifier.align(Alignment.BottomCenter))
    }

    if (showReport) {
        ReportSheet(
            title = stringResource(Res.string.gems_report_title),
            reasons = listOf(
                ReportReason(stringResource(Res.string.gems_report_reason_offensive), "offensive"),
                ReportReason(stringResource(Res.string.gems_report_reason_spam), "spam"),
                ReportReason(stringResource(Res.string.gems_report_reason_other), "other"),
            ),
            status = when (state.reportStatus) {
                GemReportStatus.Sending -> ReportStatus.Sending
                GemReportStatus.Sent -> ReportStatus.Sent
                GemReportStatus.Failed -> ReportStatus.Failed
                else -> ReportStatus.Idle
            },
            sentText = stringResource(Res.string.gems_report_sent),
            failedText = stringResource(Res.string.gems_report_failed),
            submitText = stringResource(Res.string.gems_report_submit),
            doneText = stringResource(Res.string.gems_report_done),
            onSubmit = { reason -> onReport(reason) },
            onDismiss = { showReport = false; onResetReport() },
        )
    }
}
